// ============================================
// COMPONENT - Modal cierre forzado de un turno ajeno (propietario)
// ============================================

import { useState } from 'react';
import { Button } from '@almacenes/ui';
import type { TurnoListItem } from '@almacenes/core';
import { MdClose } from 'react-icons/md';
import { formatMontoInput } from '../utils/formato';

interface ForzarCierreModalProps {
  turno: TurnoListItem;
  onClose: () => void;
  onConfirmar: (motivo: string, efectivoContado: number | undefined) => void;
  isLoading: boolean;
}

export const ForzarCierreModal = ({ turno, onClose, onConfirmar, isLoading }: ForzarCierreModalProps) => {
  const [motivo, setMotivo] = useState('');
  const [montoDisplay, setMontoDisplay] = useState('');
  const [montoRaw, setMontoRaw] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const handleMontoChange = (value: string) => {
    const soloNumeros = value.replace(/\D/g, '');
    setMontoRaw(soloNumeros);
    setMontoDisplay(formatMontoInput(soloNumeros));
  };

  const handleConfirmar = () => {
    if (!motivo.trim()) {
      setError('El motivo del cierre forzado es obligatorio');
      return;
    }
    onConfirmar(motivo.trim(), montoRaw ? Number(montoRaw) : undefined);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-2xl font-bold" style={{ color: '#11194F' }}>Cierre forzado</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <MdClose className="text-2xl" />
          </button>
        </div>

        <p className="text-gray-600 mb-4">
          Vas a forzar el cierre del turno de <strong>{turno.usuarioNombre}</strong>.
        </p>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-800">
            Los movimientos pendientes de este turno se rechazarán automáticamente y no podrás
            revertir esta acción.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Motivo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={motivo}
              maxLength={255}
              onChange={(e) => { setMotivo(e.target.value); if (error) setError(undefined); }}
              placeholder="Turno olvidado, cajero ausente, etc."
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                error ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
              }`}
              disabled={isLoading}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Efectivo contado <span className="text-gray-400">(opcional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={montoDisplay}
                onChange={(e) => handleMontoChange(e.target.value)}
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-6">
          <Button variant="secondary" onClick={onClose} disabled={isLoading} className="flex-1">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmar} isLoading={isLoading} disabled={isLoading} className="flex-1">
            Forzar cierre
          </Button>
        </div>
      </div>
    </div>
  );
};
